/**
 * SKY SCENE — composes sky primitives driven by the hour-based sky clock.
 *
 * Sandboxes and the Canvas 2D fallback (jsdom / no-WebGL) draw through this
 * module. Live play uses the GPU sky mesh with the same hour/layout uniforms.
 */

import { drawSky, skyColorAt } from "./sky/sky-gradient.js";
import { drawHorizonBleed, drawSea, drawSeaGlare } from "./sky/sea.js";
import { drawStars, starFieldAlpha } from "./sky/stars.js";
import { drawClouds } from "./sky/clouds.js";
import { drawSun, drawMoon } from "./sky/celestial.js";
import {
  computeMockupUiScale,
  mockupSunRadiusPx,
  mockupMoonRadiusPx,
} from "../layout/mockup-ui-scale.js";
import { resolveGridWidthPx } from "../layout/responsive-board.js";
import { combineSkyDecorSeed, isCelestialDiscVisible } from "../core/day-night.js";
import { computeCelestialState, decomposeHour, hourToColorPhase } from "../core/sky-clock.js";

function scaleUiFromLayout(layout, bounds) {
  var uiW = layout ? resolveGridWidthPx(layout) : bounds.width;
  if (!(uiW > 0)) uiW = bounds.width;
  return computeMockupUiScale(uiW).scaleUi;
}

/**
 * Animation "seconds" that elapse per in-game hour. All decor motion (clouds,
 * stars, sea waves, glare shimmer) is driven by this hour-based clock instead of
 * wall-clock time, so the scene is frozen whenever the hour is held still and
 * speeds up exactly when the hour is advanced quickly (e.g. the day/night
 * time-lapse transitions).
 */
var ANIM_SECONDS_PER_HOUR = 6;

/** True when the star field should be visible for this hour and layout. */
export function shouldDrawSkyStarsForHour(hour, layout, bounds) {
  if (!bounds) return false;
  layout = layout || {};
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  if (starFieldAlpha(state.colorPhase) <= 0.01) return false;
  var seaLineY = state.anchors.seaLineY;
  return Math.max(0, seaLineY - bounds.y) > 0;
}

/**
 * Stars composited after the sky/sea cache so star glow is not repainted on every
 * sky cache miss. Uses the hour-based decor clock (same as drawSkyScene).
 */
export function drawSkyStarsForScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var layout = opts.layout || {};
  var runSeed = opts.runSeed || 0;
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  var seaLineY = state.anchors.seaLineY;
  var skyBand = {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: Math.max(0, seaLineY - bounds.y),
  };
  if (skyBand.height <= 0) return;
  var animMs = state.hour * ANIM_SECONDS_PER_HOUR * 1000;
  drawStars(ctx, skyBand, state.colorPhase, animMs, runSeed, {
    dpr: opts.dpr,
    legacy: opts.starLegacy === true,
  });
}

/**
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} hour
 * @param {object} [opts]
 */
export function buildSkySceneContext(bounds, hour, opts) {
  opts = opts || {};
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return null;

  var layout = opts.layout || {};
  var runSeed = opts.runSeed || 0;
  var scaleUi = scaleUiFromLayout(layout, bounds);
  var hourPhase = decomposeHour(hour);
  var bodyRadius = hourPhase.isDay
    ? mockupSunRadiusPx(scaleUi)
    : mockupMoonRadiusPx(scaleUi);
  var state = computeCelestialState(hour, layout, { bodyRadius: bodyRadius });
  var seaLineY = state.anchors.seaLineY;
  var animSec = state.hour * ANIM_SECONDS_PER_HOUR;
  var animMs = animSec * 1000;
  var cloudSeed = combineSkyDecorSeed(runSeed, 0);
  var skyBand = {
    x: bounds.x,
    y: bounds.y,
    width: w,
    height: Math.max(0, seaLineY - bounds.y),
  };
  var pos = state.position;
  var discVisible = isCelestialDiscVisible(
    pos.y,
    seaLineY,
    bodyRadius,
    state.bodyProgress
  );
  var emergence = bodyRadius > 0
    ? clamp01((seaLineY - (pos.y - bodyRadius)) / (2 * bodyRadius))
    : (discVisible ? 1 : 0);

  return {
    bounds: bounds,
    layout: layout,
    runSeed: runSeed,
    scaleUi: scaleUi,
    state: state,
    t: state.colorPhase,
    seaLineY: seaLineY,
    animSec: animSec,
    animMs: animMs,
    cloudSeed: cloudSeed,
    skyBand: skyBand,
    pos: pos,
    bodyRadius: bodyRadius,
    discVisible: discVisible,
    emergence: emergence,
  };
}

/**
 * Sky gradient + stars (behind moon/clouds). Cached separately in the live game.
 */
export function drawSkyBackdropScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) return;
  drawSky(ctx, bounds, scene.t);
  if (opts.drawStars !== false) {
    drawSkyStarsForScene(ctx, bounds, hour, {
      layout: scene.layout,
      runSeed: scene.runSeed,
      dpr: opts.dpr,
      starLegacy: opts.starLegacy === true,
    });
  }
}

/**
 * Moon/sun, clouds, sea, and glare — composited over the backdrop.
 */
export function drawSkyForegroundScene(ctx, bounds, hour, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) return;

  var state = scene.state;
  var t = scene.t;
  var seaLineY = scene.seaLineY;
  var animSec = scene.animSec;
  var animMs = scene.animMs;
  var cloudSeed = scene.cloudSeed;
  var skyBand = scene.skyBand;
  var pos = scene.pos;
  var bodyRadius = scene.bodyRadius;
  var discVisible = scene.discVisible;
  var emergence = scene.emergence;
  var layout = scene.layout;
  var scaleUi = scene.scaleUi;

  var showCelestial = opts.drawCelestial !== false;
  var showClouds = opts.drawClouds !== false;

  // Horizon soften sits *behind* the sun/moon so a setting disc is not covered
  // by the blue fade. Opaque sea (below) still occludes the submerged half.
  drawHorizonBleed(ctx, bounds, t, { seaLineY: seaLineY });

  // 2. Active body — drawn BEFORE the clouds so they pass in front of it. Clipped
  //    to the sky band so the flat sea line cleanly cuts the disc as it sets.
  if (showCelestial) {
    ctx.save();
    if (skyBand.height > 0 && discVisible) {
      ctx.beginPath();
      ctx.rect(skyBand.x, skyBand.y, skyBand.width, skyBand.height);
      ctx.clip();
      if (state.isDay) {
        drawSun(ctx, pos.x, pos.y, bodyRadius, t);
      } else {
        // Sample the sky behind the moon so its dark side reflects the actual sky
        // colour at its current height (blends into the background as it travels).
        var moonSkyFrac = bounds.height > 0 ? (pos.y - bounds.y) / bounds.height : 0;
        var moonSky = skyColorAt(t, moonSkyFrac);
        drawMoon(ctx, pos.x, pos.y, bodyRadius, { skyColor: moonSky });
      }
    }
    ctx.restore();
  }

  // 3. Clouds drift in front of the sun/moon. The day↔night colour sweeps
  //    vertically with the hour (white grows up from the bottom at sunrise,
  //    night creeps down from the top at sunset).
  // Spread the cloud band down to ~10% below the top of the playable area, so
  // clouds can sit anywhere from the top of the sky to just inside the play box.
  if (showClouds) {
    var play = layout.playArea || layout.coreRect || bounds;
    var cloudBandBottomY = play.y + play.height * 0.10;
    // Light the clouds from the visible body: warm from the sun, cool from the
    // moon, brighter the closer/higher it is and fading out as it sets.
    var cloudLight = showCelestial && discVisible
      ? {
          x: pos.x,
          y: pos.y,
          glowRadius: bodyRadius * 8,
          color: state.isDay ? [255, 232, 158] : [200, 220, 255],
          strength: (state.isDay ? 1.05 : 0.5) * (0.3 + 0.7 * emergence),
        }
      : null;
    var warm = cloudWarm(state.phase);
    drawClouds(ctx, bounds, animMs, cloudSeed, {
      layout: layout,
      timeOfDay: t,
      cloudDayBoundary: cloudDayBoundary(state.phase),
      cloudBandBottomY: cloudBandBottomY,
      cloudLight: cloudLight,
      cloudWarmth: warm.amount,
      cloudWarmColors: warm.colors,
    });
  }

  // 4. Sea on top of the band edge — waves scroll on the hour clock. Painting it
  //    after the body is what visually occludes the submerged portion.
  drawSea(ctx, bounds, t, { seaLineY: seaLineY, waveTime: animSec, scaleUi: scaleUi });

  // 5. Glare column under whichever body is up — grows wider and fades in as the
  //    body clears the horizon (thin at the first sliver, full once risen).
  if (showCelestial && discVisible && emergence > 0.001) {
    var eased = emergence * emergence * (3 - 2 * emergence); // smoothstep
    drawSeaGlare(ctx, bounds, {
      celestialX: pos.x,
      seaLineY: seaLineY,
      isMoon: !state.isDay,
      currentTime: animMs,
      strength: (state.isDay ? 1 : 0.85) * eased,
      widthScale: 0.22 + 0.78 * eased,
    });
  }
}

/**
 * Draw the full sky/sea scene for a continuous hour.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds full canvas
 * @param {number} hour continuous day/night hour (0..72+)
 * @param {{
 *   layout: object,        // { coreRect, playArea, leftStrip, rightStrip }
 *   runSeed?: number,      // per-run seed; varies decor per run
 *   drawStars?: boolean,   // default true
 *   drawClouds?: boolean,  // default true
 *   drawCelestial?: boolean, // sun/moon + sea glare; default true
 * }} opts
 */
export function drawSkyScene(ctx, bounds, hour, opts) {
  drawSkyBackdropScene(ctx, bounds, hour, opts);
  drawSkyForegroundScene(ctx, bounds, hour, opts);
}

function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

/**
 * Vertical day/night boundary for the clouds, as a fraction from the cloud's top
 * (0 = all day/white, 1 = all night-coloured; night sits on top, day on bottom).
 *  - phase 0..2:  sunrise — white grows up from the bottom, fully white by hour 2.
 *  - phase 2..10: full day (white).
 *  - phase 10..12: sunset — night darkens down from the top.
 *  - phase 12..24: full night colour.
 * @param {number} phase in-cycle hour 0..24
 */
export function cloudDayBoundary(phase) {
  if (phase < 2) return 1 - phase / 2;
  if (phase < 10) return 0;
  if (phase < 12) return (phase - 10) / 2;
  return 1;
}

// Golden-hour cloud palettes (top → mid → underside). Sunrise leans yellow/gold;
// sunset leans deeper orange/peach.
var SUNRISE_WARM = { top: [255, 252, 240], mid: [255, 240, 200], bot: [253, 224, 165] };
var SUNSET_WARM = { top: [255, 236, 206], mid: [255, 198, 150], bot: [248, 152, 118] };

/**
 * Golden-hour warmth for the clouds: intensity (0 = none, 1 = peak) plus the
 * palette to tint with. Intensity is a smooth bump around sunrise (phase ~1) and
 * sunset (phase ~11.5), zero through midday and the deep night; a third bump near
 * phase 24 covers the pre-dawn glow that wraps to the next cycle's sunrise. The
 * sunrise/pre-dawn bumps use the yellow palette, sunset the orange one.
 * @param {number} phase in-cycle hour 0..24
 * @returns {{ amount: number, colors: { top: number[], mid: number[], bot: number[] } }}
 */
export function cloudWarm(phase) {
  var rise = Math.max(warmBump(phase - 1), warmBump(phase - 24));
  var set = warmBump(phase - 11.5);
  return {
    amount: clamp01(Math.max(rise, set)),
    colors: rise >= set ? SUNRISE_WARM : SUNSET_WARM,
  };
}

/**
 * Hour → draw opts for a single cloud (boundary sweep, golden wash, night blend).
 * Does not include `cloudLight` — callers place a synthetic or scene light.
 * @param {number} hour continuous day/night hour
 * @returns {{
 *   timeOfDay: number,
 *   cloudDayBoundary: number,
 *   cloudWarmth: number,
 *   cloudWarmColors: { top: number[], mid: number[], bot: number[] },
 *   isDay: boolean,
 *   phase: number,
 *   bodyProgress: number,
 * }}
 */
export function cloudAppearanceForHour(hour) {
  var d = decomposeHour(hour);
  var warm = cloudWarm(d.phase);
  return {
    timeOfDay: hourToColorPhase(hour),
    cloudDayBoundary: cloudDayBoundary(d.phase),
    cloudWarmth: warm.amount,
    cloudWarmColors: warm.colors,
    isDay: d.isDay,
    phase: d.phase,
    bodyProgress: d.bodyProgress,
  };
}

/** Smooth 0..1 bump, 1 at delta=0, fading to 0 over a ±3h half-width. */
function warmBump(delta) {
  var d = Math.abs(delta) / 3;
  if (d >= 1) return 0;
  var u = 1 - d;
  return u * u * (3 - 2 * u);
}
